<script setup lang="ts">
import type { InfoItemsFieldsFragment } from '~/api/graphql/generated'

const props = defineProps<{
  entity: InfoItemsFieldsFragment
}>()

const itemCount = computed(() => props.entity?.fieldInfoItem?.length || 0)

const type = computed(() => props.entity.fieldBackgroundColor || 'light-blue')

const gridClass = computed(() => {
  if (itemCount.value <= 3) {
    return `grid-${itemCount.value}`
  }
  return 'grid-4'
})
</script>

<template>
  <Section v-if="entity?.fieldInfoItem?.length" :id="entity.fieldId ?? undefined" :class="`type-${type}`">
    <div class="background" />
    <ul
      v-animate="{ cluster: true, animation: 'vertical-reveal' }"
      :class="['info-items', gridClass, 'cul', 't-10', 't-o-1', 'tl-12', 'tl-o-0', 'ds-10', 'ds-o-1']"
    >
      <li
        v-for="(item, index) in entity.fieldInfoItem"
        :key="index"
        v-animate="{ animation: 'vertical-reveal' }"
        class="item"
      >
        <div v-if="item" class="label">{{ item.label }}</div>
        <div v-if="item" class="f-60 f-medium" v-html="item.value" />
        <div v-if="item?.description" class="text-small">{{ item.description }}</div>
      </li>
    </ul>
  </Section>
</template>

<style scoped lang="scss">
.section {
  --background-color: var(--c-red-dark-100);
  --text-color: var(--c-white);
  --item-background-color: var(--c-background-light);

  position: relative;
  padding: 36px;

  @include breakpoint(tl) {
    padding: 0;
  }

  &.type-light-blue {
    --background-color: var(--c-background-medium);
    --text-color: var(--c-primary);
    --item-background-color: var(--c-white);

    .info-items .item .text-small {
      color: var(--c-gray-100);
      opacity: 1;
    }
  }

  &.type-dark-blue {
    --background-color: var(--c-primary);
    --text-color: var(--c-white);
    --item-background-color: var(--c-white);
  }

  &.type-petrol {
    --background-color: var(--c-green-dark-100);
    --text-color: var(--c-white);
    --item-background-color: var(--c-white);
  }

  .background {
    @include fill;
    z-index: -1;
    background: var(--background-color);
  }
}

.info-items {
  display: grid;
  gap: 36px;
  margin: 0;
  list-style: none;

  @include breakpoint(tl) {
    gap: 24px;
  }

  @include breakpoint(ds) {
    gap: 32px;
  }

  &.grid-1 {
    @include breakpoint(tl) {
      grid-template-columns: 1fr;
      max-width: 400px;
      margin: 0 auto;
    }
  }

  &.grid-2 {
    @include breakpoint(tl) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  &.grid-3 {
    @include breakpoint(tl) {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  &.grid-4 {
    @include breakpoint(tl) {
      grid-template-columns: repeat(2, 1fr);
    }

    @include breakpoint(ds) {
      grid-template-columns: repeat(4, 1fr);
    }
  }

  .item {
    position: relative;
    text-align: left;
    background-color: transparent;
    color: var(--text-color);
    padding: 0;

    @include breakpoint(tl) {
      padding: 48px 0px 64px 64px;
    }

    .label {
      margin-bottom: 16px;
      letter-spacing: 1.5px;
      color: inherit;

      @include breakpoint(ts down) {
        font-size: 12px;
        margin-bottom: 12px;
      }

      @include breakpoint(tl) {
        margin-bottom: 16px;
      }
    }

    .f-60 {
      margin-bottom: 0px;
      vertical-align: middle;
      color: inherit;

      @include breakpoint(ts down) {
        font-size: 38px;
        line-height: 44px;
      }
    }

    .text-small {
      margin-top: 0;
      color: inherit;
      opacity: 0.8;
    }
  }
}
</style>
